export type ClassValue = string | string[] | null | undefined;

export type VariantValue = string | string[] | null | undefined;

export interface VariantDefinition {
  base?: ClassValue;
  variants?: Record<string, Record<string, ClassValue>>;
  defaultVariants?: Record<string, string>;
  compoundVariants?: Array<{
    variants: Record<string, VariantValue>;
    class: ClassValue;
  }>;
}

export interface ComponentDefinition extends VariantDefinition {
  slots?: Record<string, VariantDefinition>;
}

type ResolveVariantsInput = Record<string, string | undefined>;

const toArray = (value: ClassValue): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const matchesVariant = (resolved: ResolveVariantsInput, target: VariantValue, key: string): boolean => {
  const current = resolved[key];
  if (target == null) return false;
  if (Array.isArray(target)) return target.includes(current ?? '');
  return current === target;
};

const buildClasses = (definition: VariantDefinition, resolved: ResolveVariantsInput): string[] => {
  const classes: string[] = [];

  classes.push(...toArray(definition.base));

  if (definition.variants) {
    for (const [variantName, values] of Object.entries(definition.variants)) {
      const variantValue = resolved[variantName] ?? definition.defaultVariants?.[variantName];
      if (!variantValue) continue;
      const variantClass = values[variantValue];
      if (variantClass) {
        classes.push(...toArray(variantClass));
      }
    }
  }

  if (definition.compoundVariants) {
    for (const compound of definition.compoundVariants) {
      const isMatch = Object.entries(compound.variants).every(([name, expected]) =>
        matchesVariant(resolved, expected, name),
      );
      if (isMatch) {
        classes.push(...toArray(compound.class));
      }
    }
  }

  return classes;
};

export type VariantResult =
  | string
  | {
      root: string;
      [slot: string]: string;
    };

export const createComponentVariants =
  (definition: ComponentDefinition) =>
  (inputVariants: ResolveVariantsInput = {}): VariantResult => {
    const resolved = { ...(definition.defaultVariants ?? {}), ...inputVariants };

    const rootClasses = buildClasses(definition, resolved).join(' ').trim();

    if (!definition.slots) {
      return rootClasses;
    }

    const slots = { root: rootClasses } as { root: string; [slot: string]: string };

    for (const [slotName, slotDefinition] of Object.entries(definition.slots)) {
      slots[slotName] = buildClasses(slotDefinition, resolved).join(' ').trim();
    }

    return slots;
  };
